import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES, SITE, waLink, WA_DEFAULT_MSG } from "../config/site";

const QUICK_LINKS = [
  { to: "/", label: "Home", testid: "footer-home-link" },
  { to: "/about", label: "About Us", testid: "footer-about-link" },
  { to: "/destinations", label: "Destinations", testid: "footer-destinations-link" },
  { to: "/services", label: "Travel Services", testid: "footer-services-link" },
  { to: "/plan-your-trip", label: "Plan Your Trip", testid: "footer-plan-trip-link" },
  { to: "/admin", label: "Admin Login", testid: "nav-admin-link" },
];

export const Footer = () => (
  <footer className="bg-ocean text-white">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <Logo light />
        <p className="mt-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
          ARORATRAVEL ONLINE LLP
        </p>
        <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/60">
          A travel agency based in {SITE.city}, helping individuals, families
          and businesses plan their journeys.
        </p>
        {SITE.socials.map((s) => (
          <a
            key={s.label}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-coral hover:text-white"
          >
            <Instagram className="h-4 w-4 text-coral" />
            {s.handle}
          </a>
        ))}
      </div>

      <nav aria-label="Footer navigation">
        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Explore</h3>
        <ul className="mt-4 space-y-2.5">
          {QUICK_LINKS.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                data-testid={link.testid}
                className="text-sm text-white/70 transition-colors hover:text-coral"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Our Services</h3>
        <ul className="mt-4 space-y-2.5">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/plan-your-trip?service=${encodeURIComponent(s.title)}`}
                className="text-sm text-white/70 transition-colors hover:text-coral"
              >
                {s.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contact</h3>
        <ul className="mt-4 space-y-3 text-sm text-white/70">
          <li className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
            <span>{SITE.address}</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
            <span>{SITE.phoneDisplay}</span>
          </li>
          <li>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email-link"
              className="flex items-start gap-2.5 transition-colors hover:text-white"
            >
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
              <span>{SITE.email}</span>
            </a>
          </li>
          <li>
            <a
              href={waLink(WA_DEFAULT_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp: {SITE.whatsappDisplay}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <p className="mx-auto max-w-7xl px-4 pr-14 pt-5 pb-24 text-center text-xs text-white/50 sm:px-6 sm:pb-5">
        © {new Date().getFullYear()} ARORATRAVEL ONLINE LLP. All rights reserved.
      </p>
    </div>
  </footer>
);
